<template>
  <div class="data-grid">
    <!-- Top row -->
    <div v-if="showSettings" class="d-flex flex-nowrap gutter mb-3">
      <div class="left-col">
        <div class="settings-control-container">
          <b-button
            variant="primary"
            @click="saveCalcProp"
            class="btn btn-sm btn-outline-primary nav-button"
            :disabled="saveAsCalcPropertyDisabled"
          >
          Save as a calculated property
          </b-button>
          <button
            class="btn btn-sm btn-outline-primary nav-button"
            @click="toggleShowSettings"
          >
            {{ hideSettingsText }}
          </button>
          <download-csv
            :data="csvData"
            class="btn btn-sm btn-outline-primary nav-button"
          >
            Export to csv
          </download-csv>
          <button
            style="width: 100%"
            class="btn btn-sm btn-outline-primary mb-1"
            v-on:click="loadMore"
            v-if="isLoadMore"
          >
            Load more
          </button>
          <button
            v-on:click="reload"
            class="btn btn-sm btn-outline-success nav-button mt-2"
          >
            Run report
          </button>
        </div>
        <div class="row">
          <b-form-checkbox
            class="pull-left and-or-rule__hidden"
            v-model="isHighlightEnabled"
            @change="toggleHighlightAsHeatmap()"
          >
            highlight as heatmap
          </b-form-checkbox>
        </div>
        <div class="row">
          <div class="col-md-12 d-flex justify-content-between align-items-center pl-0 mt-1">
            <div class="mb-2">Calc column</div>
            <b-dropdown
              size="sm"
              class="text-ellipsis"
              v-bind:text="currentCalcColumn.name"
            >
              <b-dropdown-item
                v-for="(calcColumn, key) in calcColumns"
                :key="key"
                v-bind:class="{
                  active: calcColumn.name === currentCalcColumn.name,
                }"
                @click="changeCurrentCalcColumn(calcColumn)"
              >
                {{ calcColumn.name }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
        <div v-if="currentCalcColumn.name === 'Expression'" class="row">
          <div class="col-md-12">
            <div class="mb-2">Expression</div>
            <b-form-input
              v-model="currentCalcColumn.expression"
              @change="changeCurrentCalcColumn(currentCalcColumn)"
              type="text"
            />
          </div>
        </div>
        <div class="row">
          <div v-if="currentOperation" class="col-md-12 d-flex justify-content-between align-items-center pl-0 mt-1">
            <div class="mb-2">Operation</div>
            <b-dropdown
              size="sm"
              class="text-ellipsis"
              v-bind:text="currentOperation.label"
            >
              <b-dropdown-item
                v-for="(operation, key) in operations"
                :key="key"
                v-bind:class="{
                  active: operation.label === currentOperation.label,
                }"
                @click="changeCurrentOperation(operation)"
              >
                {{ operation.label }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
        <br />
        <!--
        <div style="position: absolute;">
          <p class="data-grid__itemsPerPageControl col-md-12 pl-0 mt-3 mb-0">
            <span>Rows per page:</span>
            <b-form-select
              v-model="itemsPerPage"
              @change="setNumOfItemsPerPage($event)"
              size="sm"
             >
              <option :value="row" v-for="row in rowsPerPage">{{ row }}</option>
             </b-form-select>
          </p>
          <p class="data-grid__itemsPerPageControl col-md-12 pl-0 mt-1 mb-0">
            Rows total: {{ totalItems }}
          </p>
          <p class="data-grid__itemsPerPageControl col-md-12 pl-0 mt-1 mb-0">
            Page: {{ numOfPage }}
          </p>
        </div>
        -->
      </div>
      <div class="settings">
        <!-- Filters area -->
        <div class="where-statement">
          <div class="mb-2">
            Edit Filters
            <span class="toggle-filter-settings" @click="toggleFilterSettings">
              {{ showFilterSettings ? 'Hide' : 'Show' }}
            </span>
          </div>
          <and-or
            v-if="showFilterSettings"
            :options="options"
            :group="currentConditionGroup"
            :isFirst="true"
            :dataProvider="dataProvider"
            @change-statement="onChangedStatement()"
            ref="filtersBuilder"
          ></and-or>
        </div>
        <!-- Sql editor area -->
        <div class="where-statement">
          <div class="mb-2">
            Query
            <span
              :isFirst="true"
              class="toggle-filter-settings"
              @click="toggleSQLSettings"
            >
              {{ showSQLSettings ? 'Hide' : 'Show' }}
            </span>
          </div>
          <SQLEditor
            :sql="sql"
            v-if="showSQLSettings"
            @changeSQLQuery="changeSQLQuery"
            ref="queryBuilder"
          ></SQLEditor>
        </div>

        <div class="where-statement">
          <div class="mb-2">
            <TableHeadingFilterBuilder
              style="margin-left: 0"
              :gridId="dataProvider"
              :filter="currentConditionGroup"
              @change="applyFilterBuilder(filterBuilder)"
            />
          </div>
        </div>

        <!-- Available fields -->
        <div class="flex-fill drag-area" :class="dragAreaClass">
          <div class="mb-2">{{ availableFieldsLabelText }}</div>
          <draggable
            v-model="internal.fields"
            class="d-flex flex-row gutter-sm drag-area-zone"
            group="fields"
            @start="start"
            @end="end"
          >
            <div v-for="field in internal.fields" :key="field.key">
              <div class="btn btn-draggable btn-secondary">
                <svg
                  aria-hidden="true"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 270 512"
                  class="svg-inline--fa fa-grip-vertical fa-w-10"
                >
                  <path
                    fill="currentColor"
                    d="M64 208c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM16 104c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48zm0 304c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48z M204 208c26.5 0 48 21.5 48 48s-21.5 48 -48 48 -48 -21.5 -48 -48 21.5 -48 48 -48zM156 104c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48zm0 304c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48z"
                    class=""
                  ></path>
                </svg>
                {{ field.label }}
              </div>
            </div>
          </draggable>
        </div>
      </div>
    </div>

    <div v-else class="mb-3">
      <button
        class="btn btn-sm btn-outline-primary"
        @click="toggleShowSettings"
      >
        {{ showSettingsText }}
      </button>
    </div>

    <div class="d-flex gutter mb-3" v-if="showSettings">
      <!-- Top left zone - TODO: renderer select menu -->
      <div class="left-col"></div>

      <!-- Column fields -->
      <div class="flex-fill drag-area border-primary" :class="dragAreaClass">
        <div class="mb-2">{{ colsLabelText }}</div>
        <draggable
          v-model="internal.colFields"
          class="d-flex flex-row gutter-sm drag-area-zone"
          group="fields"
          @start="start"
          @end="end"
        >
          <div v-for="field in internal.colFields" :key="field.key">
            <div class="btn btn-draggable btn-primary">
              <svg
                aria-hidden="true"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 270 512"
                class="svg-inline--fa fa-grip-vertical fa-w-10"
              >
                <path
                  fill="currentColor"
                  d="M64 208c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM16 104c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48zm0 304c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48z M204 208c26.5 0 48 21.5 48 48s-21.5 48 -48 48 -48 -21.5 -48 -48 21.5 -48 48 -48zM156 104c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48zm0 304c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48z"
                  class=""
                ></path>
              </svg>
              {{ field.label }}
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <div class="d-flex flex-nowrap gutter">
      <!-- Row fields -->
      <div v-if="showSettings" class="left-col">
        <div class="drag-area border-primary" :class="dragAreaClass">
          <div class="mb-2">{{ rowsLabelText }}</div>
          <draggable
            v-model="internal.rowFields"
            class="d-flex flex-column align-items-start gutter-sm drag-area-zone"
            group="fields"
            @start="start"
            @end="end"
          >
            <div v-for="field in internal.rowFields" :key="field.key">
              <div class="btn btn-draggable btn-primary">
                <svg
                  aria-hidden="true"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 270 512"
                  class="svg-inline--fa fa-grip-vertical fa-w-10"
                >
                  <path
                    fill="currentColor"
                    d="M64 208c26.5 0 48 21.5 48 48s-21.5 48-48 48-48-21.5-48-48 21.5-48 48-48zM16 104c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48zm0 304c0 26.5 21.5 48 48 48s48-21.5 48-48-21.5-48-48-48-48 21.5-48 48z M204 208c26.5 0 48 21.5 48 48s-21.5 48 -48 48 -48 -21.5 -48 -48 21.5 -48 48 -48zM156 104c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48zm0 304c0 26.5 21.5 48 48 48s48 -21.5 48 -48 -21.5 -48 -48 -48 -48 21.5 -48 48z"
                    class=""
                  ></path>
                </svg>
                {{ field.label }}
              </div>
            </div>
          </draggable>
        </div>
      </div>

      <!-- Table -->
      <div class="flex-fill" :style="tableWrapperStyle">
        <pivot-table
          v-if="data"
          :data="data"
          :no-data-warning-text="noDataWarningText"
          :is-data-loading="isDataLoading"
        ></pivot-table>
      </div>
    </div>
  </div>
</template>

<script>
import PivotTable from './PivotTable'
import SQLEditor from './SQLEditor/SQLEditor'
import AndOr from './FilterBuilder/AndOR'
import TableHeadingFilterBuilder from './../DataGrid/table/TableHeadingFilterBuilder'
import Draggable from 'vuedraggable'

export default {
  name: 'vue-pivot',
  components: {
    PivotTable,
    Draggable,
    AndOr,
    SQLEditor,
    TableHeadingFilterBuilder,
  },
  props: {
    saveAsCalcPropertyDisabled: {
      type: Boolean
    },
    saveCalcProp: {
      type: Function
    },
    reload: {
      type: Function,
    },
    itemsPerPage: {
      type: Number,
    },
    rowsPerPage: {
      type: Array,
    },
    rowsPages: {
      type: Number
    },
    csvData: {
      type: Array,
    },
    loadMore: {
      type: Function,
    },
    isLoadMore: {
      type: Boolean,
    },
    totalItems: {
      type: Number,
    },
    numOfPage: {
      type: Number,
    },
    prevRow: {
      type: Function,
    },
    nextRow: {
      type: Function,
    },
    prevCol: {
      type: Function,
    },
    nextCol: {
      type: Function,
    },
    rowPage: {
      type: Number,
    },
    rowPages: {
      type: Number,
    },
    colPage: {
      type: Number,
    },
    colPages: {
      type: Number,
    },
    data: {
      type: Array,
      default: [],
    },
    sql: {
      type: String,
    },
    dataProvider: {
      type: String,
    },
    fields: {
      type: Array,
    },
    rowFields: {
      type: Array,
    },
    colFields: {
      type: Array,
    },
    showSettings: {
      type: Boolean,
    },
    showFilterSettings: {
      type: Boolean,
    },
    showSQLSettings: {
      type: Boolean,
    },
    highlightAsHeatmap: {
      type: Boolean,
    },
    availableFieldsLabelText: {
      type: String,
      default: 'Available fields',
    },
    colsLabelText: {
      type: String,
      default: 'Columns',
    },
    rowsLabelText: {
      type: String,
      default: 'Rows',
    },
    hideSettingsText: {
      type: String,
      default: 'Hide settings',
    },
    showSettingsText: {
      type: String,
      default: 'Show settings',
    },
    noDataWarningText: {
      type: String,
      default: 'No data to display.',
    },
    isDataLoading: {
      type: Boolean,
      default: false,
    },
    calcColumns: {
      type: Array,
    },
    operations: {
      type: Array,
    },
    currentOperation: {
      type: Object,
    },
    currentCalcColumn: {
      type: Object,
    },
    currentConditionGroup: {
      type: Object,
    },
  },
  data: function() {
    return {
      headingShown: false,
      internal: {
        fields: this.fields,
        rowFields: this.rowFields,
        colFields: this.colFields,
      },
      dragging: false,
      isHighlightEnabled: this.highlightAsHeatmap,
      options: {
        dataProvider: this.dataProvider,
        keys: [],
        operators: [
          {
            name: 'more',
            id: '<',
            except: ['lookup'],
          },
          {
            name: 'equal',
            id: '=',
          },
          {
            name: 'not equal',
            id: '!=',
          },
          {
            name: 'less',
            id: '>',
            except: ['lookup'],
          },
          {
            name: 'IN',
            id: 'in',
            onlyFor: ['lookup'],
          },
        ],
      },
    }
  },
  mounted() {
    this.updateInternal()
  },
  watch: {
    fields(val) {
      if (val) {
        this.updateInternal()
      }
    },
  },
  computed: {
    dragAreaClass: function() {
      return this.dragging ? 'drag-area-highlight' : null
    },
    tableWrapperStyle: function() {
      const maxWidth = this.showSettings ? 'calc(100% - 200px - 2rem)' : '100%'
      return `max-width: ${maxWidth};`
    },
  },
  methods: {
    updateInternal() {
      const allFields = []
      const fields = this.fields
      const rowFields = this.rowFields
      const colFields = this.colFields

      this.internal = {
        fields: fields,
        rowFields: rowFields,
        colFields: colFields,
      }

      !fields
        ? null
        : fields.map(field =>
            allFields.push({
              name: field.label,
              id: field.key,
            })
          )

      !colFields
        ? null
        : colFields.map(field =>
            allFields.push({
              name: field.label,
              id: field.key,
            })
          )

      !rowFields
        ? null
        : rowFields.map(field =>
            allFields.push({
              name: field.label,
              id: field.key,
            })
          )

      this.options = {
        ...this.options,
        keys: [
          {
            name: 'Choose Key',
            id: -1,
          },
          ...allFields.map(field => field),
        ],
      }
    },
    toggleShowSettings() {
      const showSettings = !this.showSettings
      this.$emit('changeShowSetting', showSettings)
    },
    toggleFilterSettings() {
      const showFilterSettings = !this.showFilterSettings
      this.$emit('changeShowFilterSetting', showFilterSettings)
    },
    toggleSQLSettings() {
      const showSQLSettings = !this.showSQLSettings
      this.$emit('changeShowSQLSettings', showSQLSettings)
    },
    toggleHighlightAsHeatmap() {
      setTimeout(() =>
        this.$emit('changeHighlightAsHeatmap', this.isHighlightEnabled)
      )
    },
    start: function() {
      this.dragging = true
    },
    end: function() {
      this.dragging = false
    },
    changeCurrentOperation(operation) {
      this.$emit('changeCurrentOperation', operation)
    },
    changeCurrentCalcColumn(calcColumn) {
      this.$emit('changeCurrentCalcColumn', calcColumn)
    },
    changeSQLQuery(query) {
      this.$emit('changeSQLQuery', query)
    },
    onChangedStatement() {
      setTimeout(() => {
        const statement = this.$refs.filtersBuilder.queryFormStatus()
        this.$emit('changeCurrentConditionGroup', statement)
      })
    },
    applyFilterBuilder(filterBuilder) {
      const statement = filterBuilder !== undefined ? filterBuilder : []
      this.$emit('changeCurrentConditionGroup', statement)
    },
  },
}
</script>

<style lang="scss" scoped>
.text-ellipsis {
  /deep/ {
    .dropdown-toggle {
      white-space: nowrap;
      overflow: hidden;
      max-width: 200px;
      display: block;
      text-overflow: ellipsis;
    }
  }
  color: #5e72e4;
  border: 1px solid #5e72e4;
  border-radius: 4px;
}
/* Left column width */
.left-col {
  min-width: 200px;
  max-width: 200px;
}
.settings {
  flex-grow: 1;
}
.settings-control-container {
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

/* Grid with gutter */
.gutter,
.gutter-y {
  margin-top: -1rem;

  > * {
    margin-top: 1rem;
  }
}
.gutter-x,
.gutter {
  margin-left: -1rem;
  > * {
    margin-left: 1rem;
  }
}

.gutter-sm,
.gutter-y-sm {
  margin-top: -0.5rem;

  > * {
    margin-top: 0.5rem;
  }
}
.gutter-x-sm,
.gutter-sm {
  margin-left: -0.5rem;
  > * {
    margin-left: 0.5rem;
  }
}

/* Drag & drop stuff */
.drag-area {
  border: 1px dashed #ccc;
  padding: 0.75rem;
  transition: background-color 0.4s;

  .drag-area-zone {
    min-width: 10rem;
    min-height: 3rem;
    flex-wrap: wrap;
  }

  .btn-draggable {
    cursor: move !important;
  }
}

.drag-area-highlight {
  background-color: #f3f3f3;
}

.sortable-ghost {
  opacity: 0.4;
}

/* Handle icon (mix of grip-vertical & ellipsis-v) */
.svg-inline--fa.fa-w-10 {
  width: 0.625em;
}

.svg-inline--fa {
  display: inline-block;
  font-size: inherit;
  height: 1em;
  overflow: visible;
  vertical-align: -0.125em;
}

.btn-draggable .fa-grip-vertical {
  margin-left: -0.375rem;
  margin-right: 0.375rem;
}

/* Draggable buttons */
.btn-draggable {
  white-space: normal;
  text-align: left;
}

.toggle-filter-settings {
  border-bottom: 1px dashed #558697;
  color: #558697;
  display: inline-block;
  margin-left: 10px;
  cursor: pointer;
}
.controll {
  cursor: pointer;
  color: #5e72e4;
}
.controll:hover {
  font-weight: bold;
}
.nav-button {
  margin-bottom: 10px;
  margin-right: 0;
}
</style>
